import { Center } from "@chakra-ui/layout"
import { Box, Divider, Flex, Grid, Heading, Link } from "@chakra-ui/react"
import { t } from "i18next"
import { FC, useContext, useEffect, useRef, useState, useMemo } from "react"
import { useHistory } from "react-router-dom"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import useConfig from "src/hooks/useConfig"
import useSearchParams from "src/hooks/useSearchParams"
import {
  journeyUiSelector,
  useReturnToJourneyAction
} from "src/recoil/atoms/journeyUI"
import axios from "src/shared/axios"
import { HIDE_GRID_TITLE, Views } from "src/constants"
import { SDKContext } from "src/context/SDKContext"
import { useValidateRoom } from "src/hooks/useValidateRoom"
import { appUserIdAtom } from "src/recoil/atoms/app_user"
import {
  AvatarDimensionality,
  avatarsErrorMessageAtom,
  avatarsLoading,
  avatarsSelector,
  gridHeaderSearchTextAtom,
  gridSelectedAvatarAtom,
  gridSelectedAvatarDimensionalityAtom,
  navigatedFromAvatarsListAtom
} from "src/recoil/atoms/avatars"
import { AvatarConfig, AvatarDetail } from "src/recoil/atoms/avatars/types"
import { loadingRoomHashAtom } from "src/recoil/atoms/shows"
import { viewSelector } from "src/recoil/atoms/view"
import { addCurrentQueryParams, parseLocalStorage } from "src/util"
import LoadingView from "../../loading"
import ShowCardSkeleton from "../../shows/show-card-skeleton"
import ShowsErrorMessage from "../../shows/shows-error-message"
import usePageStyling from "src/hooks/usePageStyling"
import ConditionalAvatarCard from "src/conditionals/Avatar/ConditionalAvatarCard"
import useAuthedUser from "src/hooks/useAuthedUser"
import { ConsoleLogger } from "src/util/logger"
import { useInspirations } from "src/hooks/useInspirations"
import { Inspirations } from "../components/Inspirations"
import { queuedInspirationAtom } from "src/recoil/atoms/inspirations"
import { usePersonalRoom } from "src/hooks/usePersonalRoom"
import InspirationCategoriesRow from "../components/AvatarCategoriesRow"
import { InspirationCategoryModel } from "src/hooks/useInspirationCategories"
import { AuthPageStyling } from "src/context/types"
import { useIsMobileApp } from "src/util/deviceType"

const logger = new ConsoleLogger()

const AvatarGrid: FC = () => {
  const [inspirationCategoryFilter, setInspirationCategoryFilter] =
    useState<InspirationCategoryModel | null>(null)
  const { inspirations } = useInspirations({
    filterByCategoryId: inspirationCategoryFilter?.id
  })

  const config = useConfig()
  const history = useHistory()
  const params = useSearchParams<{ go_back: "journey" }>()
  const returnRoute = params.go_back
  const navigatedFromJourney = returnRoute === "journey"
  let featureEnabled = config.avatarsGrid || navigatedFromJourney
  const appUserId = useRecoilValue(appUserIdAtom)
  const { personalRoom } = usePersonalRoom()

  const branding: AuthPageStyling = usePageStyling("authPageStyling")

  const [loading] = useRecoilState(avatarsLoading)

  const meetingClient = useContext(SDKContext)
  const { validateRoom } = useValidateRoom(meetingClient)
  const showsErrorMessage = useRecoilValue(avatarsErrorMessageAtom)
  const [loadingRoomHash, setLoadingRoomHash] =
    useRecoilState(loadingRoomHashAtom)
  const setNavigatedFromAvatarsList = useSetRecoilState(
    navigatedFromAvatarsListAtom
  )
  const selectedRoomHash = useRef<string | null>(null)
  const [view, setView] = useRecoilState(viewSelector)
  const roomHash = personalRoom?.moderatorHash
  const { authedUser, getAuthedUser } = useAuthedUser()
  const setQueuedInspiration = useSetRecoilState(queuedInspirationAtom)

  const { avatars } = useRecoilValue(avatarsSelector) || ({} as AvatarDetail)
  console.log(avatars)

  // Filter avatar list using search text
  const gridHeaderSearchText = useRecoilValue(gridHeaderSearchTextAtom)
  const filteredAvatars = useMemo(() => {
    if (!gridHeaderSearchText) return avatars
    // Filter base on avatar name
    return avatars?.filter(avatar => {
      return avatar.avatarName
        .toLowerCase()
        .includes(gridHeaderSearchText.toLowerCase().trim())
    })
  }, [gridHeaderSearchText, avatars])

  const setGridSelectedAvatarDimensionality = useSetRecoilState(
    gridSelectedAvatarDimensionalityAtom
  )
  const setGridSelectedAvatar = useSetRecoilState(gridSelectedAvatarAtom)
  const isMobileApp = useIsMobileApp()

  useEffect(() => {
    getAuthedUser()
  }, [])

  useEffect(() => {
    if (appUserId !== null)
      meetingClient?.nativeBridge?.eventSetAppUserID({ appUserID: appUserId })
  }, [appUserId])

  // useEffect(() => {
  //   const count = sessionStorage.getItem("reloadCount")
  //   const reloadAllowed = +count < 1
  //   if (isOffline) {
  //     sessionStorage.setItem("reloadCount", "0")
  //   }
  //   if (!isOffline && reloadAllowed) {
  //     // reload the page
  //     history.go(0)
  //     sessionStorage.setItem("reloadCount", "1")
  //   }
  // }, [isOffline])

  /** Navigate to provide roomhash and clear the cache */
  const navigateToRoomHash = (roomhash: string) => {
    if (isMobileApp) {
      history.replace(addCurrentQueryParams(roomhash))
    } else {
      history.push(addCurrentQueryParams(roomhash))
    }
    selectedRoomHash.current = null
    setLoadingRoomHash(null)
  }

  /** This useEffect is use to navigate to roomhash, after the meeting is joined */
  useEffect(() => {
    if (selectedRoomHash.current) {
      navigateToRoomHash(selectedRoomHash.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoomHash.current])

  /** Handle on click on avatar card. This function open the given room hash meeting */
  const handleOpenRoom = async (avatarConfig: AvatarConfig) => {
    localStorage.setItem("avatarConfig", JSON.stringify(avatarConfig))
    setGridSelectedAvatar(avatarConfig)
    // Disconnect the previous meeting if active
    // await meetingClient?.disconnectGateway(0)

    meetingClient?.setRoomHash(roomHash)
    setLoadingRoomHash(roomHash)
    selectedRoomHash.current = roomHash

    // If the user is inside the meeting, then navigate directly by displaying fullscreen loader
    // if (view === Views.MEETING_ROOM) {
    //   setView(Views.LOADING)
    //   navigateToRoomHash(roomHash)
    // }

    // Directly join the room if user has already join the room before, to check that we are using username.
    // If user name is present we are joining the room directly
    const joinRoom = !!authedUser?.screenName
    if (joinRoom) meetingClient?.setUserName(authedUser?.screenName ?? "User")
    await validateRoom({ joinRoomDirectly: joinRoom })

    // If we are not join room direct then navigate to home page, So that user can set userName
    if (!joinRoom) {
      navigateToRoomHash(roomHash)
      setView(Views.HOME)
    } else {
      navigateToRoomHash(roomHash)
      setView(Views.MEETING_ROOM)
    }

    // set true so that on leave we can redirect user to show screen
    setNavigatedFromAvatarsList(true)
  }

  /** Connect the websocket when component is loaded */
  useEffect(() => {
    if (meetingClient && featureEnabled) {
      console.log("Connecting avatars")
      meetingClient.connectAvatars(axios)
    }
  }, [meetingClient])

  const setJourneyUi = useSetRecoilState(journeyUiSelector)

  const earlyAvatarSelect = (avatarConfig: AvatarConfig) => {
    localStorage.setItem("avatarConfig", JSON.stringify(avatarConfig))
    setJourneyUi({ transition: "externalToJourney" })
  }

  useReturnToJourneyAction()

  // If error occur, display error message
  if (showsErrorMessage) return <ShowsErrorMessage {...showsErrorMessage} />
  if (loading && featureEnabled) return <LoadingView />

  return (
    <Box
      width="full"
      height="full"
      display={"flex"}
      flexDirection="column"
      // backgroundColor={branding?.authPageBackgroundColor ?? "gray.100"}
      // backgroundImage={
      //   branding?.authPageBackgroundImage
      //     ? `url(${branding?.authPageBackgroundImage})`
      //     : "none"
      // }
      backgroundSize={"cover"}
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      overflowY="auto"
      boxSizing="border-box"
      position="relative"
    >
      <Flex justify={"center"} pos="sticky" paddingTop={"4px"}>
        <Flex
          direction={"column"}
          width="100%"
          // width={["100%", null, "86%"]}
          // maxWidth="1000px"
          pb={["12px", "12px", "16px"]}
        >
          <InspirationCategoriesRow onChanged={setInspirationCategoryFilter} />

          <Box width="full" pt={["12px", null, "14px"]}>
            {inspirations && (
              <Inspirations
                onClick={inspiration => {
                  // const inspirationAvatar:AvatarConfig = {...inspiration.avatar, avatarId: inspiration.defaultAvatarId}
                  let avatarToLoad = inspiration.avatar
                  if (
                    inspiration.defaultAvatarId == inspiration.avatar.avatarId
                  ) {
                    // 2d
                    setGridSelectedAvatarDimensionality(
                      AvatarDimensionality.twoDimensional
                    )
                  } else if (
                    inspiration.defaultAvatarId == inspiration.avatar.avatarId3d
                  ) {
                    // 3d
                    setGridSelectedAvatarDimensionality(
                      AvatarDimensionality.threeDimensional
                    )
                  } else {
                    // defaultAvatarId was 0/not set.
                    // let's load the user's last used avatar if it exists, otherwise let's load the first in the list
                    const dimensionality = parseLocalStorage(
                      "lastUsedAvatarDimensionality",
                      AvatarDimensionality.twoDimensional
                    )
                    setGridSelectedAvatarDimensionality(dimensionality)
                    const lastLoadedAvatar =
                      parseLocalStorage<AvatarConfig | null>(
                        "lastUsedAvatarConfig",
                        null
                      )

                    avatarToLoad = lastLoadedAvatar ?? avatars[0]
                  }
                  handleOpenRoom(avatarToLoad)
                  setQueuedInspiration(inspiration)
                }}
                inspirations={inspirations.filter(
                  inspiration =>
                    inspiration.title
                      .toLowerCase()
                      .includes(gridHeaderSearchText.toLowerCase()) ||
                    inspiration.description
                      .toLowerCase()
                      .includes(gridHeaderSearchText.toLowerCase()) ||
                    inspiration.avatar.avatarName
                      .toLowerCase()
                      .includes(gridHeaderSearchText.toLowerCase())
                )}
              />
            )}
          </Box>
        </Flex>
      </Flex>

      <Box
        width="100%"
        overflowY="auto"
        flexGrow={1}
        className="hide-scrollbar"
      >
        {window.location.search.includes("journey") && (
          <>
            <Heading
              fontSize="3xl"
              textAlign="center"
              color="white"
              width="100%"
              lineHeight={["1.8rem", "1.33"]}
              mt="58px"
            >
              {t("Choose your avatar teacher")}
            </Heading>
            <Center
              width="160px"
              mt="12px"
              borderColor="#faf703"
              display="flex"
              flexDir="column"
              rowGap="2px"
            >
              <Divider
                borderTop="1px solid #faf703"
                borderBottom="1px solid #faf703"
                height="2px"
                opacity={1}
              />
            </Center>
          </>
        )}

        <Grid
          gridGap={["8px", "12px", "12px"]}
          gridTemplateColumns={[
            "repeat(2, 1fr)",
            "repeat(3, 1fr)",
            "repeat(4, 1fr)"
          ]}
          paddingBottom="12px"
          alignContent="flex-start"
          paddingTop={HIDE_GRID_TITLE ? "8" : "initial"}
          // mt="12px"
          width="100%"
        >
          {filteredAvatars?.map(avatarConfig => (
            <ConditionalAvatarCard
              key={avatarConfig.avatarName}
              avatarConfig={avatarConfig}
              btnText={returnRoute === "journey" ? "Select" : "Chat"}
              openRoom={
                returnRoute === "journey"
                  ? () => earlyAvatarSelect(avatarConfig)
                  : () => handleOpenRoom(avatarConfig)
              }
              roomHash={roomHash}
              loadingRoomHash={loadingRoomHash}
              branding={branding}
            />
          ))}

          {avatars?.length === 0 &&
            Array(4)
              .fill(0)
              .map((_, i) => (
                <ShowCardSkeleton
                  key={`show-card-skeleton-${i}`}
                  listLength={avatars.length}
                />
              ))}
        </Grid>
      </Box>
    </Box>
  )
}

export default AvatarGrid
